import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { creationsApi, type TaskBrief } from "@/entities/advisor-chat";

// 出成品的终态(红点语义=有新「出好的作品」;FAILED/RUNNING 不算)。
const PRODUCED = new Set(["SUCCESS", "PARTIAL"]);
const keyOf = (kind: string, t: TaskBrief) => `${kind}-${t.id}`;

/**
 * 关闭态出图完成红点(问题3):launcher 层轮询最近作品,出现比「已看过」更新的出成品任务 → hasUnseen。
 * 首帧 seed 历史完成不闪;打开抽屉(open=true)即 acknowledge 当前全部、清红点。
 */
export function useCreationBadge(open: boolean, enabled = true) {
  const [hasUnseen, setHasUnseen] = useState(false);
  const ackRef = useRef<Set<string> | null>(null); // null = 未 seed

  const posterQ = useQuery({
    queryKey: ["advisor", "badge", "poster"],
    queryFn: () => creationsApi.listPosters(),
    enabled, // 无军师权限用户不轮询(launcher 对其 return null)
    refetchInterval: 8000,
  });
  const videoQ = useQuery({
    queryKey: ["advisor", "badge", "video"],
    queryFn: () => creationsApi.listVideos(),
    enabled,
    refetchInterval: 8000,
  });

  // 两查询首次都返回才算加载完成:否则空集 seed 会把首帧回来的历史完成误判为「新」而误亮(codex P2)。
  const loaded = posterQ.isFetched && videoQ.isFetched;
  const producedKeys = [
    ...(posterQ.data?.records ?? [])
      .filter((t) => PRODUCED.has(t.status))
      .map((t) => keyOf("poster", t)),
    ...(videoQ.data?.records ?? [])
      .filter((t) => PRODUCED.has(t.status))
      .map((t) => keyOf("video", t)),
  ];
  const sig = producedKeys.join(",");

  // 新出成品(不在 ack)→ 亮红点。加载完成后首帧仅 seed(不为历史闪)。
  useEffect(() => {
    if (!loaded) return; // 等两查询首次返回再 seed
    if (ackRef.current === null) {
      ackRef.current = new Set(producedKeys);
      return;
    }
    if (producedKeys.some((k) => !ackRef.current!.has(k))) setHasUnseen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, sig]);

  // 打开抽屉即 acknowledge 全部 + 清红点(加载完成后)。
  useEffect(() => {
    if (!open || !loaded) return;
    ackRef.current = new Set(producedKeys);
    setHasUnseen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loaded, sig]);

  return { hasUnseen };
}
